import { useEffect, useMemo, useState } from "react";
import {
  Users, Plus, Upload, Send, Search, X, Copy, Mail, MousePointerClick,
  Loader2, Archive, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { PortalShell } from "@/components/PortalShell";
import { formatDateAU } from "@/lib/formatDateAU";

const STATUS_PILLS = {
  new: { bg: "bg-slate-100", text: "text-slate-700", label: "New" },
  invited: { bg: "bg-cyan-100", text: "text-cyan-800", label: "Invited" },
  clicked: { bg: "bg-blue-100", text: "text-blue-800", label: "Clicked" },
  applied: { bg: "bg-amber-100", text: "text-amber-800", label: "Applied" },
  converted: { bg: "bg-emerald-100", text: "text-emerald-800", label: "Converted" },
  declined: { bg: "bg-rose-100", text: "text-rose-700", label: "Declined" },
};

const Pill = ({ status }) => {
  const s = STATUS_PILLS[status] || STATUS_PILLS.new;
  return <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>{s.label}</span>;
};

const EMPTY_FORM = { business_name: "", contact_name: "", email: "", phone: "", category: "", region: "", source: "", notes: "" };

export default function PartnerProspects() {
  const [data, setData] = useState({ prospects: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (q) params.q = q;
      const { data } = await api.get("/admin/partner-prospects", { params });
      setData(data);
    } catch { toast.error("Could not load prospects"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter]);
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
  }, [q]);

  const toggleSel = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allVisible = data.prospects.map((p) => p.id);
  const allChecked = allVisible.length > 0 && allVisible.every((id) => selected.has(id));

  return (
    <PortalShell>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-prospects-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">Partner Acquisition</div>
          <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">Prospects</h1>
          <p className="text-slate-500 mt-1 text-sm">Import, track and invite businesses to the AWARE Partner Network.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="prospects-import-btn" onClick={() => setShowImport(true)} className="rounded-full font-semibold">
            <Upload className="w-4 h-4 mr-1.5" /> Import CSV
          </Button>
          <Button data-testid="prospects-add-btn" onClick={() => setShowAdd(true)} className="btn-aqua rounded-full font-semibold">
            <Plus className="w-4 h-4 mr-1.5" /> Add prospect
          </Button>
        </div>
      </div>

      {/* status summary chips */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="prospects-status-chips">
        <button onClick={() => setStatusFilter("")}
          className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${!statusFilter ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
          All · {data.total}
        </button>
        {Object.entries(STATUS_PILLS).map(([key, s]) => (
          <button key={key} data-testid={`prospects-filter-${key}`} onClick={() => setStatusFilter(statusFilter === key ? "" : key)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${statusFilter === key ? "bg-slate-900 text-white border-slate-900" : `${s.bg} ${s.text} border-transparent hover:border-slate-300`}`}>
            {s.label} · {data.counts?.[key] || 0}
          </button>
        ))}
      </div>

      {/* toolbar */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="prospects-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by business, contact or email…" className="pl-10 h-10 rounded-xl" />
        </div>
        {selected.size > 0 && (
          <Button data-testid="prospects-bulk-invite-btn" onClick={() => setShowInvite(true)} className="btn-aqua rounded-full font-semibold">
            <Send className="w-4 h-4 mr-1.5" /> Invite {selected.size} selected
          </Button>
        )}
      </div>

      {/* table */}
      <div className="mt-4 bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
            <tr>
              <th className="px-4 py-3 w-10">
                <Checkbox data-testid="prospects-select-all" checked={allChecked}
                  onCheckedChange={(v) => setSelected(v ? new Set(allVisible) : new Set())} />
              </th>
              <th className="text-left px-4 py-3">Business</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Source</th>
              <th className="text-left px-4 py-3">Last invited</th>
              <th className="text-left px-4 py-3">Clicks</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="text-center py-10 text-slate-500">Loading…</td></tr>
            ) : data.prospects.length === 0 ? (
              <tr><td colSpan="7" className="text-center py-12 text-slate-500">
                <Users className="w-7 h-7 mx-auto mb-2 text-slate-300" />
                No prospects yet. Add one or import a CSV to get started.
              </td></tr>
            ) : data.prospects.map((p) => (
              <tr key={p.id} data-testid={`prospect-row-${p.id}`}
                className="border-t border-slate-100 hover:bg-slate-50/50 cursor-pointer"
                onClick={() => setDetail(p)}>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleSel(p.id)}
                    data-testid={`prospect-check-${p.id}`} />
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{p.business_name}</div>
                  <div className="text-xs text-slate-500">{p.email}</div>
                </td>
                <td className="px-4 py-3 text-slate-700">{p.contact_name || "—"}</td>
                <td className="px-4 py-3"><Pill status={p.status} /></td>
                <td className="px-4 py-3 text-xs text-slate-500">{p.source || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{p.last_invited_at ? formatDateAU(p.last_invited_at) : "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {p.click_count > 0 ? (
                    <span className="inline-flex items-center gap-1"><MousePointerClick className="w-3 h-3" />{p.click_count}</span>
                  ) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AddProspectDialog open={showAdd} onClose={() => setShowAdd(false)} onSaved={load} />
      <ImportCsvDialog open={showImport} onClose={() => setShowImport(false)} onImported={load} />
      <InviteDialog open={showInvite} onClose={() => setShowInvite(false)} ids={[...selected]}
        onSent={() => { setSelected(new Set()); load(); }} />
      {detail && (
        <ProspectDetail prospect={detail} onClose={() => setDetail(null)} onChanged={load}
          onInvite={() => { setSelected(new Set([detail.id])); setDetail(null); setShowInvite(true); }} />
      )}
    </div>
    </PortalShell>
  );
}

function AddProspectDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.business_name || !form.email) return toast.error("Business name and email are required");
    setSaving(true);
    try {
      await api.post("/admin/partner-prospects", form);
      toast.success("Prospect added");
      setForm(EMPTY_FORM);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add prospect");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="prospect-add-dialog">
        <DialogHeader><DialogTitle>Add prospect</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {[["business_name", "Business name *"], ["contact_name", "Contact name"], ["email", "Email *"], ["phone", "Phone"], ["category", "Category"], ["region", "Region / State"], ["source", "Source"]].map(([k, label]) => (
            <div key={k} className={k === "business_name" || k === "email" ? "col-span-2" : ""}>
              <Label className="text-xs font-semibold text-slate-600">{label}</Label>
              <Input data-testid={`prospect-form-${k}`} value={form[k]} onChange={set(k)} className="mt-1 h-10 rounded-xl" />
            </div>
          ))}
          <div className="col-span-2">
            <Label className="text-xs font-semibold text-slate-600">Notes</Label>
            <Textarea data-testid="prospect-form-notes" value={form.notes} onChange={set("notes")} rows={2} className="mt-1 rounded-xl" />
          </div>
        </div>
        <Button data-testid="prospect-form-save" onClick={save} disabled={saving} className="btn-aqua rounded-full font-semibold w-full">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add prospect"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function ImportCsvDialog({ open, onClose, onImported }) {
  const [csvText, setCsvText] = useState("");
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(f);
  };

  const run = async () => {
    if (!csvText.trim()) return toast.error("Paste CSV text or choose a file");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/partner-prospects/import-csv", { csv_text: csvText, source: source || undefined });
      setResult(data);
      toast.success(`Imported ${data.imported} · skipped ${data.skipped_duplicates} duplicates`);
      onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setResult(null); setCsvText(""); onClose(); } }}>
      <DialogContent className="max-w-xl" data-testid="prospect-import-dialog">
        <DialogHeader><DialogTitle>Import prospects from CSV</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">
          Needs an <code>email</code> column. Recognised: business/company, contact, email, phone,
          category/type, region/state, source, notes. Duplicates (by email) are skipped.
        </p>
        <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="prospect-import-file"
          className="text-xs text-slate-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border-0 file:bg-cyan-50 file:text-cyan-700 file:font-semibold file:text-xs" />
        <Textarea data-testid="prospect-import-text" value={csvText} onChange={(e) => setCsvText(e.target.value)}
          rows={7} placeholder={"business_name,contact_name,email,phone\nAcme Plumbing,Jane Doe,jane@acme.example,0400000000"}
          className="rounded-xl font-mono text-xs" />
        <Input data-testid="prospect-import-source" value={source} onChange={(e) => setSource(e.target.value)}
          placeholder="Source label (optional, e.g. 'qld-plumbers-jun26')" className="h-10 rounded-xl" />
        {result && (
          <div className="text-xs rounded-xl bg-slate-50 border border-slate-200 p-3" data-testid="prospect-import-result">
            Imported <b>{result.imported}</b> · duplicates skipped <b>{result.skipped_duplicates}</b>
            {result.errors?.length > 0 && <div className="mt-1 text-rose-600">{result.errors.slice(0, 5).join("; ")}</div>}
          </div>
        )}
        <Button data-testid="prospect-import-run" onClick={run} disabled={busy} className="btn-aqua rounded-full font-semibold w-full">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Import"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function InviteDialog({ open, onClose, ids, onSent }) {
  const [campaign, setCampaign] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/partner-prospects/invite", {
        ids, campaign: campaign || undefined, message: message || undefined,
      });
      toast.success(`Sent ${data.sent} invitation${data.sent !== 1 ? "s" : ""}${data.failed ? ` · ${data.failed} failed` : ""}`);
      onSent();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send invitations");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="prospect-invite-dialog">
        <DialogHeader><DialogTitle>Send Partner invitation{ids.length !== 1 ? `s (${ids.length})` : ""}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">
          Each prospect receives a personal email with their unique tracked application link.
          Clicks and applications are attributed automatically.
        </p>
        <div>
          <Label className="text-xs font-semibold text-slate-600">Campaign label (optional)</Label>
          <Input data-testid="invite-campaign" value={campaign} onChange={(e) => setCampaign(e.target.value)}
            placeholder="e.g. jun26-rural-suppliers" className="mt-1 h-10 rounded-xl" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-600">Personal intro (optional, shown at top of email)</Label>
          <Textarea data-testid="invite-message" value={message} onChange={(e) => setMessage(e.target.value)}
            rows={3} placeholder="Great meeting you at the expo — here's the partner info I mentioned." className="mt-1 rounded-xl" />
        </div>
        <Button data-testid="invite-send" onClick={send} disabled={busy || ids.length === 0} className="btn-aqua rounded-full font-semibold w-full">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Send invitation{ids.length !== 1 ? "s" : ""}</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function ProspectDetail({ prospect, onClose, onChanged, onInvite }) {
  const [p, setP] = useState(prospect);
  const [notes, setNotes] = useState(prospect.notes || "");
  // Aug 2026 — per-invitation engagement timeline (opens are approximate)
  const [timeline, setTimeline] = useState(null);
  useEffect(() => {
    api.get(`/admin/partner-prospects/${prospect.id}/timeline`)
      .then(({ data }) => setTimeline(data)).catch(() => {});
  }, [prospect.id]);
  const inviteUrl = useMemo(
    () => `${window.location.origin}/trade/register?invite=${p.invite_token}`,
    [p.invite_token]
  );

  const saveNotes = async () => {
    try {
      const { data } = await api.put(`/admin/partner-prospects/${p.id}`, { notes });
      setP(data);
      toast.success("Notes saved");
      onChanged();
    } catch { toast.error("Could not save notes"); }
  };

  const setStatus = async (status) => {
    try {
      const { data } = await api.put(`/admin/partner-prospects/${p.id}`, { status });
      setP(data);
      onChanged();
      toast.success(`Marked ${status}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  const archive = async () => {
    try {
      await api.delete(`/admin/partner-prospects/${p.id}`);
      toast.success("Prospect archived");
      onChanged();
      onClose();
    } catch { toast.error("Could not archive"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex justify-end" onClick={onClose} data-testid="prospect-detail-drawer">
      <div className="w-full max-w-md bg-white h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900">{p.business_name}</h2>
            <div className="text-sm text-slate-500">{p.contact_name || "—"} · {p.email}</div>
            <div className="mt-2"><Pill status={p.status} /></div>
          </div>
          <button onClick={onClose} data-testid="prospect-detail-close" className="p-1.5 rounded-full hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
          {[["Phone", p.phone], ["Category", p.category], ["Region", p.region], ["Source", p.source]].map(([label, v]) => (
            <div key={label} className="rounded-xl bg-slate-50 p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{label}</div>
              <div className="mt-0.5 text-slate-800">{v || "—"}</div>
            </div>
          ))}
        </div>

        <div className="mt-5">
          <Label className="text-xs font-semibold text-slate-600">Tracked invite link</Label>
          <div className="mt-1 flex items-center gap-2">
            <Input readOnly value={inviteUrl} className="h-9 rounded-xl text-xs" data-testid="prospect-invite-url" />
            <Button size="sm" variant="outline" className="rounded-full shrink-0" data-testid="prospect-copy-link"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(inviteUrl);
                  toast.success("Link copied");
                } catch {
                  toast.error("Copy failed — select the link manually");
                }
              }}>
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <Button size="sm" onClick={onInvite} data-testid="prospect-send-invite" className="btn-aqua rounded-full font-semibold">
            <Mail className="w-3.5 h-3.5 mr-1.5" /> {p.last_invited_at ? "Resend invite" : "Send invite"}
          </Button>
          {p.status !== "declined" && (
            <Button size="sm" variant="outline" data-testid="prospect-mark-declined" onClick={() => setStatus("declined")} className="rounded-full">
              Mark declined
            </Button>
          )}
          {p.status === "declined" && (
            <Button size="sm" variant="outline" data-testid="prospect-mark-new" onClick={() => setStatus("new")} className="rounded-full">
              Re-open as new
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={archive} data-testid="prospect-archive" className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50">
            <Archive className="w-3.5 h-3.5 mr-1.5" /> Archive
          </Button>
        </div>

        {p.application_user_id && (
          <div className="mt-5 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800" data-testid="prospect-application-note">
            <ExternalLink className="w-3.5 h-3.5 inline mr-1" />
            Application received {p.applied_at ? formatDateAU(p.applied_at) : ""} — linked user <code>{p.application_user_id.slice(0, 8)}…</code>
          </div>
        )}

        <div className="mt-5">
          <Label className="text-xs font-semibold text-slate-600">Notes</Label>
          <Textarea data-testid="prospect-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1 rounded-xl" />
          <Button size="sm" variant="outline" onClick={saveNotes} data-testid="prospect-notes-save" className="mt-2 rounded-full">Save notes</Button>
        </div>

        {timeline && timeline.events?.length > 0 && (
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="prospect-timeline">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Engagement timeline</div>
            <div className="mt-2 space-y-1">
              {timeline.events.map((e, i) => (
                <div key={i} className={`flex gap-3 text-xs ${e.flag ? "text-rose-700 font-semibold" : "text-slate-700"}`} data-testid={`timeline-event-${i}`}>
                  <span className="w-32 shrink-0 tabular-nums text-slate-500">
                    {new Date(e.at).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                  </span>
                  <span>{e.label}{e.approximate ? " *" : ""}</span>
                </div>
              ))}
            </div>
            <p className="mt-2 text-[10px] text-slate-400">* Email opens are an approximate engagement indicator.</p>
          </div>
        )}

        <div className="mt-6">
          <div className="text-xs font-semibold text-slate-600">Invite history</div>
          {(p.invites || []).length === 0 ? (
            <div className="mt-1 text-xs text-slate-400">No invitations sent yet.</div>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {[...p.invites].reverse().map((inv, i) => (
                <li key={i} className="text-xs text-slate-600 rounded-lg bg-slate-50 px-3 py-2">
                  <Mail className="w-3 h-3 inline mr-1.5 text-slate-400" />
                  {formatDateAU(inv.sent_at)}{inv.campaign ? ` · ${inv.campaign}` : ""}{inv.sent_by ? ` · by ${inv.sent_by}` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
